import { useApp, useUserInfo } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { AgeVerificationStatus } from "@next-platform/packages-ts-types";

import { AgeVerificationMethods, PageLayout, PageSize } from "@/components";
import Trans from "@/components/Trans/Trans";
import { NotFound } from "@/pages";

// The updated age verification page logic:
// - On load, if age verification is needed, call onUserGetAgeVerificationMethods to retrieve available verification methods (with methodRefs).
// - List the available methods for the user.
// - When a user clicks "Choose", call onUserPostAgeVerificationInitialize with the selected method's methodRef.
// - When the initialize API responds, redirect user to the providerUrl returned in the response.
const AgeVerification = () => {
  const { isNsfwEnabled } = useApp();
  const { userProfile } = useUserInfo();
  const verificationStatus = userProfile?.age_verification?.status;

  if (!isNsfwEnabled) {
    return <NotFound />;
  }

  return (
    <>
      <PageLayout size={PageSize.Medium} className="flex flex-col gap-xl">
        <section className="flex flex-col gap-sm">
          <Text variant={TextVariants.HEADING_4_BOLD}>
            <Trans i18nKey="ageVerification:section.main.title" />
          </Text>
          <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
            <Trans i18nKey="ageVerification:section.main.description" />
          </Text>
        </section>

        <section className="flex flex-col gap-sm">
          {verificationStatus === AgeVerificationStatus.VERIFIED && (
            <>
              <div className="flex items-center gap-sm mb-md">
                <Icon icon={IconVariants.CHECK_CIRCLE} size={IconSize.LARGE} className="text-green-10" />
                <Text variant={TextVariants.LABEL_LARGE_BOLD}>
                  <Trans i18nKey="ageVerification:section.verified.title" />
                </Text>
              </div>
              <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
                <Trans i18nKey="ageVerification:section.verified.description" />
              </Text>
            </>
          )}
          {!!verificationStatus && verificationStatus !== AgeVerificationStatus.VERIFIED && (
            <>
              <Text variant={TextVariants.LABEL_LARGE_BOLD}>
                <Trans i18nKey="ageVerification:section.selectOption.title" />
              </Text>
              <AgeVerificationMethods />
            </>
          )}
        </section>
        <section className="flex flex-col gap-sm">
          <Text variant={TextVariants.LABEL_LARGE_BOLD}>
            <Trans i18nKey="ageVerification:section.whyVerify.title" />
          </Text>
          <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
            <Trans i18nKey="ageVerification:section.whyVerify.description" />
          </Text>
        </section>

        <section className="flex flex-col gap-sm">
          <Text variant={TextVariants.LABEL_LARGE_BOLD}>
            <Trans i18nKey="ageVerification:section.privacy.title" />
          </Text>
          <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
            <Trans i18nKey="ageVerification:section.privacy.description" />
            <ul className="list-disc">
              <li>
                <Trans i18nKey="ageVerification:section.privacy.point1" />
              </li>
              <li>
                <Trans i18nKey="ageVerification:section.privacy.point2" />
              </li>
              <li>
                <Trans i18nKey="ageVerification:section.privacy.point3" />
              </li>
            </ul>
            <Trans i18nKey="ageVerification:section.privacy.footer" />
          </Text>
        </section>
        <section className="flex flex-col gap-sm">
          <Text variant={TextVariants.LABEL_LARGE_BOLD}>
            <Trans i18nKey="ageVerification:section.howItWorks.title" />
          </Text>
          <div>
            <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
              <Trans i18nKey="ageVerification:section.howItWorks.description" />
            </Text>
            <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
              <ul className="list-disc">
                <li>
                  <Trans i18nKey="ageVerification:section.howItWorks.step1" />
                </li>
                <li>
                  <Trans i18nKey="ageVerification:section.howItWorks.step2" />
                </li>
                <li>
                  <Trans i18nKey="ageVerification:section.howItWorks.step3" />
                </li>
              </ul>
            </Text>
          </div>
        </section>

        <section className="flex flex-col gap-sm">
          <Text variant={TextVariants.LABEL_LARGE_BOLD}>
            <Trans i18nKey="ageVerification:section.help.title" />
          </Text>
          <div>
            <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
              <Trans i18nKey="ageVerification:section.help.description" />
            </Text>
            <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
              <a href="https://docs.spicychat.ai/advanced/age-verification" target="_blank" className="text-blue-10 hover:underline">
                <Trans i18nKey="ageVerification:section.help.link.faq" />
              </a>
            </Text>
            <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
              <a href="https://docs.spicychat.ai/support" target="_blank" className="text-blue-10 hover:underline">
                <Trans i18nKey="ageVerification:section.help.link.support" />
              </a>
            </Text>
          </div>
        </section>
      </PageLayout>
    </>
  );
};

export default AgeVerification;
